import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { useUser } from '@/contexts/UserContext';
import { useHeader } from '@/contexts/HeaderContext';
import { supabase } from '@/lib/supabase';
import axios from 'axios';
import Loading from '../components/Loading';
import PlanSkeleton from '../components/skeletons/PlanSkeleton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* ─── Pain ↔ Phase mappings (mirrors backend PAIN_CATEGORY_MAP) ─── */
const PAIN_SCALE = { never: 0, rarely: 1, sometimes: 2, often: 3, very_often: 4 };
const CONN_SCALE = { very_connected: 0, connected: 1, neutral: 2, distant: 3, very_distant: 4 };

const PAIN_CATEGORY_MAP = {
  autopilot: ['presence', 'logistics'],
  energy: ['presence', 'logistics'],
  gestures: ['connection', 'repair'],
  connection: ['connection', 'play', 'change_of_scene'],
};

/* ─── Human-readable category labels ─── */
const CATEGORY_LABELS = {
  presence:        { it: 'presenza', en: 'presence', es: 'presencia' },
  logistics:       { it: 'organizzazione', en: 'organization', es: 'organización' },
  connection:      { it: 'connessione', en: 'connection', es: 'conexión' },
  repair:          { it: 'riparazione', en: 'repair', es: 'reparación' },
  play:            { it: 'gioco/leggerezza', en: 'play/fun', es: 'juego/diversión' },
  change_of_scene: { it: 'novità', en: 'novelty', es: 'novedad' },
};

/* ─── Phase titles (personalized using onboarding answers) ─── */
const PHASE1_TITLES = {
  autopilot: { it: 'Uscire dal pilota automatico', en: 'Breaking the autopilot', es: 'Salir del piloto automático' },
  energy:    { it: "Gestione dell'energia",        en: 'Energy management', es: 'Gestión de la energía' },
  gestures:  { it: 'Micro-gesti quotidiani',       en: 'Daily micro-gestures', es: 'Microgestos diarios' },
  connection:{ it: 'Riconnessione emotiva',        en: 'Emotional reconnection', es: 'Reconexión emocional' },
};
const PHASE2_TITLES = {
  autopilot: { it: 'Presenza attiva',     en: 'Active presence', es: 'Presencia activa' },
  energy:    { it: 'Energia condivisa',    en: 'Shared energy', es: 'Energía compartida' },
  gestures:  { it: 'Gesti che contano',    en: 'Gestures that matter', es: 'Gestos que importan' },
  connection:{ it: 'Connessione profonda', en: 'Deeper connection', es: 'Conexión profunda' },
};
const IMPROVEMENT_LABELS = {
  quality_time:        { it: 'tempo di qualità',       en: 'quality time', es: 'tiempo de calidad' },
  home_management:     { it: 'gestione della casa',    en: 'home management', es: 'gestión del hogar' },
  emotional_presence:  { it: 'presenza emotiva',       en: 'emotional presence', es: 'presencia emocional' },
  reduce_tension:      { it: 'riduzione tensioni',     en: 'tension reduction', es: 'reducción de tensiones' },
  confidence:          { it: 'sicurezza come partner',  en: 'partner confidence', es: 'seguridad como pareja' },
};
const RELATIONSHIP_GOALS = {
  married:        { it: 'Rafforzare il matrimonio ogni giorno',         en: 'Strengthen your marriage daily',     es: 'Fortalecer tu matrimonio cada día' },
  living_together:{ it: 'Crescere insieme nella convivenza',            en: 'Grow together in daily life',         es: 'Crecer juntos en la convivencia' },
  dating:         { it: 'Costruire basi solide per il futuro',          en: 'Build a strong foundation',           es: 'Construir bases sólidas para el futuro' },
  engaged:        { it: 'Prepararsi al meglio per la vita insieme',     en: 'Prepare for life together',           es: 'Prepararse para la vida en común' },
  complicated:    { it: 'Ricostruire fiducia e connessione',            en: 'Rebuild trust and connection',        es: 'Reconstruir confianza y conexión' },
};

function computePersonalizedPlan(user, l) {
  // Pain scores → same ranking as backend
  const scores = {
    autopilot:  PAIN_SCALE[user?.pain_autopilot] ?? 0,
    energy:     PAIN_SCALE[user?.pain_energy] ?? 0,
    gestures:   PAIN_SCALE[user?.pain_gestures] ?? 0,
    connection: CONN_SCALE[user?.pain_connection] ?? 0,
  };
  const sorted = Object.entries(scores).sort((a, b) => b[1] - a[1]);
  const topPain = sorted[0][0];
  const secondPain = sorted[1][0];

  // Categories targeted per phase (mirrors backend logic exactly)
  const topCats = PAIN_CATEGORY_MAP[topPain] || [];
  const secondCats = PAIN_CATEGORY_MAP[secondPain] || [];
  const blendedCats = [...new Set([...topCats, ...secondCats])];

  // Time budget
  const timeBudget = user?.time_budget_minutes || 10;
  const timePhrase = l === 'it'
    ? `${timeBudget} min/giorno`
    : l === 'es'
      ? `${timeBudget} min/día`
      : `${timeBudget} min/day`;

  // Phase 1 desc: categories targeted + time budget
  const topCatLabels = topCats.map(c => CATEGORY_LABELS[c]?.[l] || c).join(', ');
  const phase1Desc = l === 'it'
    ? `Gesti da ${timePhrase} su ${topCatLabels}`
    : l === 'es'
      ? `Gestos de ${timePhrase} sobre ${topCatLabels}`
      : `${timePhrase} gestures focused on ${topCatLabels}`;

  // Phase 2 desc: second pain categories + improvement areas
  const secondCatLabels = secondCats.map(c => CATEGORY_LABELS[c]?.[l] || c).join(', ');
  const areas = user?.improvement_areas || [];
  const topAreas = areas.slice(0, 2).map(a => IMPROVEMENT_LABELS[a]?.[l] || a);
  let phase2Desc;
  if (topAreas.length > 0) {
    phase2Desc = l === 'it'
      ? `${secondCatLabels} + focus su ${topAreas.join(' e ')}`
      : l === 'es'
        ? `${secondCatLabels} + enfoque en ${topAreas.join(' y ')}`
        : `${secondCatLabels} + focusing on ${topAreas.join(' and ')}`;
  } else {
    phase2Desc = l === 'it'
      ? `Gesti su ${secondCatLabels}`
      : l === 'es'
        ? `Gestos sobre ${secondCatLabels}`
        : `Gestures on ${secondCatLabels}`;
  }

  // Phase 3: relationship goal + blended categories
  const rel = user?.relationship_type;
  const phase3Title = RELATIONSHIP_GOALS[rel]?.[l] || (l === 'it' ? 'Crescita continua' : l === 'es' ? 'Crecimiento continuo' : 'Continuous growth');
  const blendedLabels = blendedCats.map(c => CATEGORY_LABELS[c]?.[l] || c).join(', ');
  const phase3Desc = l === 'it'
    ? `Rotazione su ${blendedLabels} — ${timePhrase}`
    : l === 'es'
      ? `Rotación entre ${blendedLabels} — ${timePhrase}`
      : `Rotating across ${blendedLabels} — ${timePhrase}`;

  // Days since onboarding
  const obAt = user?.onboarding_completed_at;
  let daysSince = 999;
  if (obAt) {
    try { daysSince = Math.floor((Date.now() - new Date(obAt).getTime()) / 86400000); } catch { daysSince = 999; }
  }
  let activePhaseIndex = 2;
  if (daysSince <= 6) activePhaseIndex = 0;
  else if (daysSince <= 20) activePhaseIndex = 1;

  const roadmapPhases = [
    {
      week: l === 'it' ? 'Settimana 1' : l === 'es' ? 'Semana 1' : 'Week 1',
      title: PHASE1_TITLES[topPain]?.[l] || (l === 'it' ? 'Fondamenta' : l === 'es' ? 'Fundamentos' : 'Foundation'),
      desc: phase1Desc,
    },
    {
      week: l === 'it' ? 'Settimana 2–3' : l === 'es' ? 'Semana 2–3' : 'Week 2–3',
      title: PHASE2_TITLES[secondPain]?.[l] || (l === 'it' ? 'Costruire presenza' : l === 'es' ? 'Construir presencia' : 'Building presence'),
      desc: phase2Desc,
    },
    {
      week: l === 'it' ? 'Settimana 4+' : l === 'es' ? 'Semana 4+' : 'Week 4+',
      title: phase3Title,
      desc: phase3Desc,
    },
  ];

  return { roadmapPhases, daysSince, activePhaseIndex };
}

const PHASE_ICONS = [
  'solar:leaf-linear',
  'solar:heart-pulse-linear',
  'solar:cup-star-linear',
];

const Plan = () => {
  const { i18n } = useTranslation();
  const { user: contextUser, token, loading: userLoading } = useUser();
  const { setHeaderProps } = useHeader();

  // Detect language — prefer localStorage, then current i18n language
  useEffect(() => {
    const savedLang = localStorage.getItem('betterhusbandUserLang');
    if (savedLang) {
      if (i18n.language !== savedLang) {
        i18n.changeLanguage(savedLang);
      }
      return;
    }
    // No explicit preference saved — keep current i18n language (defaults to 'en')
  }, [i18n]);

  const isIt = i18n.language === 'it';
  const isEs = i18n.language === 'es';
  const l = isIt ? 'it' : isEs ? 'es' : 'en';
  const pick = (it, en, es) => isIt ? it : isEs ? (es ?? en) : en;

  const [stats, setStats] = useState(null);
  const [recentGestures, setRecentGestures] = useState([]);
  const [loading, setLoading] = useState(true);

  const userId = contextUser?.user_id || contextUser?.id;

  useEffect(() => {
    setHeaderProps({
      title: isIt ? 'Il tuo percorso' : isEs ? 'Tu camino' : 'Your Journey',
      user: contextUser,
      userId,
    });
  }, [isIt, isEs, contextUser, userId, setHeaderProps]);

  const fetchStats = useCallback(async () => {
    if (!userId) return;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const authToken = session?.access_token || token;
      const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};

      const [statsRes, metricsRes, historyRes] = await Promise.all([
        axios.get(`${API}/stats/${userId}?range=30`, { headers }),
        axios.get(`${API}/metrics/${userId}`, { headers }),
        axios.get(`${API}/suggestion/history/${userId}?limit=30`, { headers }),
      ]);
      setStats({
        ...(statsRes.data || {}),
        metrics: metricsRes.data || {},
      });
      setRecentGestures(historyRes.data || []);
    } catch (e) {
      console.warn('Plan: failed to load stats', e);
    } finally {
      setLoading(false);
    }
  }, [userId, token]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  // If user context finished loading but no userId yet, stop the spinner
  useEffect(() => {
    if (!userLoading && !userId) setLoading(false);
  }, [userLoading, userId]);

  if (userLoading || loading) {
    return <PlanSkeleton />;
  }

  const { roadmapPhases, daysSince, activePhaseIndex } = computePersonalizedPlan(contextUser, l);
  const hasPlan = contextUser?.onboarding_completed || contextUser?.onboarding_completed_at || contextUser?.pain_autopilot || contextUser?.pain_energy || contextUser?.pain_gestures || contextUser?.pain_connection;

  // Single metric
  const presenceDays = stats?.presence_days ?? 0;

  return (
    <div style={{
      margin: 0,
      fontFamily: '-apple-system, BlinkMacSystemFont, "Inter", system-ui, sans-serif',
      boxSizing: 'border-box',
      overflowX: 'hidden',
    }}>
      <div style={{ maxWidth: 'clamp(390px, 70vw, 560px)', margin: '0 auto', padding: '0 0 40px' }}>

        <div style={{
          padding: '0 24px',
          paddingTop: 1,
          paddingBottom: 32,
        }}>

          {/* ═══ YOUR JOURNEY — narrative roadmap ═══ */}
          <div style={{
            fontFamily: 'Manrope-Variable', fontSize: 14, fontWeight: 600,
            color: 'var(--c-texPri)', marginBottom: 18, marginTop: 16,
          }}>
            {pick('Il tuo percorso', 'Your journey', 'Tu camino')}
          </div>

        {!hasPlan ? (
          <div style={{
            padding: '28px 20px', borderRadius: 16, textAlign: 'center',
            background: 'var(--c-surface-card)', border: '1px solid var(--c-border)',
          }}>
            <Icon icon="solar:clipboard-text-linear" style={{ width: 32, height: 32, color: 'var(--c-texSec)', marginBottom: 10 }} />
            <div style={{ fontFamily: 'Manrope-Variable', fontSize: 13, color: 'var(--c-texSec)', lineHeight: 1.5 }}>
              {pick(
                "Completa l'onboarding per generare il tuo piano personalizzato",
                'Complete onboarding to generate your personalized plan',
                'Completa el onboarding para generar tu plan personalizado'
              )}
            </div>
          </div>
        ) : (
          <div>
            {roadmapPhases.map((phase, i) => {
              const isActive = i === activePhaseIndex;
              const isPast = i < activePhaseIndex;
              return (
                <div key={i} style={{
                  display: 'flex', alignItems: 'flex-start', gap: 16,
                  borderRadius: 16,
                  padding: '12px 14px',
                  marginBottom: 8,
                  ...(isActive ? {
                    background: 'var(--c-surface-elevated)',
                    border: '1px solid rgba(126,168,108,0.30)',
                    boxShadow: '0 0 30px rgba(126,168,108,0.06)',
                  } : {
                    background: 'var(--c-surface-card)',
                    opacity: 0.7,
                  }),
                }}>
                  {/* Timeline column */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <div style={{
                      width: 52, height: 52, borderRadius: '50%', flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: isPast
                        ? 'linear-gradient(135deg, #7EA86C, #5C8748)'
                        : isActive
                          ? 'linear-gradient(135deg, #7EA86C, #5C8748)'
                          : 'var(--c-surface-card)',
                      boxShadow: isPast
                        ? '0 4px 14px rgba(126,168,108,0.18), inset 0 -3px 0 rgba(0,0,0,0.12), inset 0 2px 0 rgba(255,255,255,0.15)'
                        : isActive
                          ? '0 0 24px rgba(126,168,108,0.20), 0 4px 14px rgba(126,168,108,0.12)'
                          : 'none',
                      border: isPast ? '3px solid #7EA86C' : isActive ? '3px solid #7EA86C' : '3px solid var(--c-border)',
                      transition: 'all 0.3s',
                    }}>
                      {isPast ? (
                        <Icon icon="solar:check-circle-bold" style={{ width: 24, height: 24, color: '#fff' }} />
                      ) : (
                        <Icon icon={PHASE_ICONS[i]} style={{ width: 24, height: 24, color: isActive ? 'var(--c-texPri)' : 'var(--c-texTer)' }} />
                      )}
                    </div>
                    {i < roadmapPhases.length - 1 && (
                      <div style={{
                        width: 2, height: 40, margin: '6px 0',
                        background: isPast ? 'var(--c-surface-elevated)' : 'var(--c-surface-card)',
                      }} />
                    )}
                  </div>

                  {/* Phase text */}
                  <div style={{ paddingTop: 4, paddingBottom: 20, flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{
                          fontFamily: 'Manrope-Variable', fontSize: 11, fontWeight: 600,
                          textTransform: 'uppercase', letterSpacing: '0.1em',
                          color: (isActive || isPast) ? 'var(--c-texPri)' : 'var(--c-texSec)',
                        }}>
                          {phase.week}
                        </span>
                        {isActive && (
                          <span style={{
                            fontFamily: 'Manrope-Variable', fontSize: 9, fontWeight: 600,
                            padding: '2px 8px', borderRadius: 999,
                            background: 'rgba(126,168,108,0.20)', color: '#7EA86C',
                          }}>
                            {pick('Ora', 'Now', 'Ahora')}
                          </span>
                        )}
                      </div>
                      {/* Leaf progress for active phase */}
                      {isActive && (() => {
                        const phaseDays = i === 0 ? 7 : i === 1 ? 14 : 21;
                        const phaseStart = i === 0 ? 0 : i === 1 ? 7 : 21;
                        const elapsed = Math.max(0, daysSince - phaseStart);
                        const filled = Math.min(5, Math.ceil((elapsed / phaseDays) * 5));
                        return (
                          <div style={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                            {[0, 1, 2, 3, 4].map(idx => (
                              <Icon
                                key={idx}
                                icon="solar:leaf-bold"
                                style={{
                                  width: 12, height: 12,
                                  color: idx < filled ? '#7EA86C' : 'var(--c-texDis)',
                                  transition: 'color 0.3s',
                                }}
                              />
                            ))}
                          </div>
                        );
                      })()}
                    </div>
                    <div style={{
                      fontFamily: 'Manrope-Variable', fontSize: 15, fontWeight: 600,
                      color: isActive ? 'var(--c-texPri)' : isPast ? 'var(--c-texPri)' : 'var(--c-texSec)',
                      marginTop: 3, lineHeight: 1.3,
                      opacity: isActive ? 1 : isPast ? 0.75 : 0.5,
                    }}>
                      {phase.title}
                    </div>
                    <div style={{
                      fontFamily: 'Manrope-Variable', fontSize: 13, fontWeight: 400,
                      color: 'var(--c-texSec)', marginTop: 2, lineHeight: 1.4,
                      opacity: isActive ? 1 : 0.6,
                    }}>
                      {phase.desc}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

          {/* ═══ RECENT GESTURES ═══ */}
          {recentGestures.length > 0 && (
            <div style={{ marginTop: 28 }}>
              <div style={{
                fontFamily: 'Manrope-Variable', fontSize: 14, fontWeight: 600,
                color: 'var(--c-texPri)', marginBottom: 14,
              }}>
                {pick('Gesti recenti', 'Recent gestures', 'Gestos recientes')}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {recentGestures.map((g, i) => {
                  const statusIcon = g.status === 'done'
                    ? 'solar:check-circle-bold'
                    : g.status === 'postponed'
                      ? 'solar:clock-circle-linear'
                      : 'solar:close-circle-linear';
                  const statusColor = g.status === 'done'
                    ? '#22C55E'
                    : g.status === 'postponed'
                      ? '#F59E0B'
                      : 'var(--c-texTer)';
                  const date = g.created_at ? new Date(g.created_at) : null;
                  const dateStr = date
                    ? new Intl.DateTimeFormat(isIt ? 'it-IT' : isEs ? 'es-ES' : 'en-US', { day: 'numeric', month: 'short' }).format(date)
                    : '';
                  return (
                    <div key={g.id || i} style={{
                      display: 'flex', alignItems: 'flex-start', gap: 12,
                      padding: '12px 14px', borderRadius: 14,
                      background: 'var(--c-surface-card)', border: '1px solid var(--c-border)',
                    }}>
                      <Icon icon={statusIcon} style={{ width: 18, height: 18, color: statusColor, flexShrink: 0, marginTop: 2 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontFamily: 'Manrope-Variable', fontSize: 13, fontWeight: 500,
                          color: 'var(--c-texPri)', lineHeight: 1.4,
                          overflow: 'hidden', textOverflow: 'ellipsis',
                          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                        }}>
                          {g.main_suggestion?.text || g.text || g.suggestion_text || pick('Gesto', 'Gesture', 'Gesto')}
                        </div>
                        {dateStr && (
                          <div style={{
                            fontFamily: 'Manrope-Variable', fontSize: 11, fontWeight: 400,
                            color: 'var(--c-texTer)', marginTop: 3,
                          }}>
                            {dateStr}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default Plan;
